import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  Modal,
  TextInput,
  NumberInput,
  Select,
  MultiSelect,
  Switch,
  Stack,
  LoadingOverlay,
  Grid,
  Table,
  ActionIcon,
  Card,
  Center,
  Alert,
  ScrollArea,
  ThemeIcon,
  SimpleGrid,
  Divider,
  Tooltip,
} from "@mantine/core";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconTicket,
  IconCalendar,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconRocket,
  IconTarget,
  IconPackage,
  IconArrowLeft,
} from "@tabler/icons-react";
import { useNotification } from "../../../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

// Descriptive fallback for a TicketType with no custom `title` set.
const TICKET_TYPE_LABELS = {
  GENERAL: "General Admission",
  VIP: "VIP",
  UPGRADE_TO_VIP: "Upgrade to VIP",
  NEW_YEAR_OFFER: "New Year Offer",
  EARLY_BIRD: "Early Bird",
  GROUP_DISCOUNT: "Group Discount",
};

// --- SUB-COMPONENT: STATS CARD ---
const StatsCard = ({ icon, label, value, color, subValue }) => (
  <Paper
    p="md"
    radius="md"
    withBorder
    className={`border-l-4 border-l-${color}-500`}
  >
    <Group>
      <ThemeIcon size="xl" radius="md" variant="light" color={color}>
        {icon}
      </ThemeIcon>
      <Box>
        <Text c="dimmed" tt="uppercase" fw={700} size="xs">
          {label}
        </Text>
        <Text fw={700} size="xl">
          {value}
        </Text>
        {subValue && (
          <Text size="xs" c="dimmed">
            {subValue}
          </Text>
        )}
      </Box>
    </Group>
  </Paper>
);

// --- SUB-COMPONENT: CREATE/EDIT MODAL ---
const CampaignCouponModal = ({
  isOpen,
  onClose,
  productId,
  onSuccess,
  initialData,
}) => {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [ticketTypes, setTicketTypes] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    code: "",
    title: "",
    couponType: "DISCOUNT",
    discountPercent: "",
    maxUses: "",
    expiryDate: "",
    imposedUserLimits: false,
    perUserLimit: "",
    isSuperCoupon: false,
    ticketTypeIds: [],
  });

  const { showSuccess, showError } = useNotification();

  // Fetch this event's ticket types so the scope dropdown can show them by
  // descriptive name — only EVENT products have any, so a non-EVENT product
  // resolves to an empty list and the dropdown is simply omitted below.
  useEffect(() => {
    if (!isOpen || !productId) return;
    fetch(
      `${API_BASE_URL}/admin/products/ticketTypes/${productId}`,
      { headers: getAuthHeaders() },
    )
      .then((res) => res.json())
      .then((data) => setTicketTypes(data.success ? data.data : []))
      .catch(() => setTicketTypes([]));
  }, [isOpen, productId]);

  // Initialize Form
  useEffect(() => {
    if (initialData) {
      setFormData({
        code: initialData.code || "",
        title: initialData.title || "",
        couponType: initialData.isFree ? "FREE" : "DISCOUNT",
        discountPercent: initialData.discountPercent || "",
        maxUses: initialData.maxUses !== null ? initialData.maxUses : "",
        expiryDate: initialData.expiryDate
          ? new Date(initialData.expiryDate).toISOString().split("T")[0]
          : "",
        imposedUserLimits: initialData.imposedUserLimit || false, // Fixed typo from original logic key mapping
        perUserLimit: initialData.perUserLimit || "",
        isSuperCoupon: initialData.isSuperCoupon || false,
        ticketTypeIds: initialData.ticketTypes
          ? initialData.ticketTypes.map((t) => t.id)
          : [],
      });
    } else {
      setFormData({
        code: "",
        title: "",
        couponType: "DISCOUNT",
        discountPercent: "",
        maxUses: "",
        expiryDate: "",
        imposedUserLimits: false,
        perUserLimit: "",
        isSuperCoupon: false,
        ticketTypeIds: [],
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        productId,
        code: formData.code.toUpperCase(),
        title: formData.title,
        isFree: formData.couponType === "FREE",
        discountPercent:
          formData.couponType === "DISCOUNT"
            ? parseInt(formData.discountPercent)
            : null,
        maxUses: formData.maxUses === "" ? null : parseInt(formData.maxUses),
        expiryDate: formData.expiryDate || null,
        imposedUserLimit: formData.imposedUserLimits,
        perUserLimit:
          formData.perUserLimit === "" ? null : parseInt(formData.perUserLimit),
        isSuperCoupon: formData.isSuperCoupon,
        ticketTypeIds: formData.ticketTypeIds,
      };

      const endpoint = isEdit
        ? `${API_BASE_URL}/admin/products/campaign-coupons/${initialData.id}`
        : `${API_BASE_URL}/admin/products/campaign-coupons`;

      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(
          isEdit ? "Updated" : "Created",
          `${formData.code} saved successfully`,
        );
        onSuccess();
        onClose();
      } else {
        showError("Error", data.error || "Operation failed");
      }
    } catch (error) {
      showError("Error", "Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          {isEdit ? "Edit Coupon" : "New Campaign Coupon"}
        </Text>
      }
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <LoadingOverlay visible={loading} />

          <Group grow>
            <TextInput
              label="Coupon Code"
              placeholder="e.g. SUMMER20"
              required
              value={formData.code}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value.toUpperCase() })
              }
              disabled={isEdit}
              fw={700}
            />
            <TextInput
              label="Internal Title"
              placeholder="e.g. Summer Sale"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
          </Group>

          {ticketTypes.length > 0 && (
            <MultiSelect
              label="Ticket Type Scope"
              description="Leave empty to apply this coupon to all ticket types"
              placeholder="All Ticket Types"
              data={ticketTypes.map((t) => ({
                value: t.id,
                label: t.title || TICKET_TYPE_LABELS[t.ticketType] || t.ticketType,
              }))}
              value={formData.ticketTypeIds}
              onChange={(v) =>
                setFormData({ ...formData, ticketTypeIds: v })
              }
            />
          )}

          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Type"
                data={[
                  { value: "DISCOUNT", label: "Percentage Discount" },
                  { value: "FREE", label: "Free (100% Off)" },
                ]}
                value={formData.couponType}
                onChange={(v) => setFormData({ ...formData, couponType: v })}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              {formData.couponType === "DISCOUNT" && (
                <NumberInput
                  label="Discount %"
                  min={1}
                  max={100}
                  required
                  value={formData.discountPercent}
                  onChange={(v) =>
                    setFormData({ ...formData, discountPercent: v })
                  }
                />
              )}
            </Grid.Col>
          </Grid>

          <Paper
            p="sm"
            bg="yellow.0"
            withBorder
            style={{ borderColor: "var(--mantine-color-yellow-2)" }}
          >
            <Switch
              label="🌟 Super Coupon (Bypass Restrictions)"
              description="Can be used on ticket types that restrict standard coupons"
              checked={formData.isSuperCoupon}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  isSuperCoupon: e.currentTarget.checked,
                })
              }
              color="yellow"
            />
          </Paper>

          <Paper p="sm" bg="gray.0" withBorder>
            <Switch
              label="Limit Uses Per User"
              checked={formData.imposedUserLimits}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  imposedUserLimits: e.currentTarget.checked,
                })
              }
              mb="xs"
            />
            {formData.imposedUserLimits && (
              <NumberInput
                label="Max Uses Per Person"
                placeholder="e.g. 1"
                min={1}
                required
                value={formData.perUserLimit}
                onChange={(v) => setFormData({ ...formData, perUserLimit: v })}
              />
            )}
          </Paper>

          <Group grow>
            <NumberInput
              label="Total Usage Limit"
              placeholder="Leave empty for unlimited"
              min={1}
              value={formData.maxUses}
              onChange={(v) => setFormData({ ...formData, maxUses: v })}
            />
            <TextInput
              type="date"
              label="Expiry Date"
              value={formData.expiryDate}
              onChange={(e) =>
                setFormData({ ...formData, expiryDate: e.target.value })
              }
            />
          </Group>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="green">
              {isEdit ? "Update Coupon" : "Create Coupon"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function CampaignCouponManagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const { showSuccess, showError } = useNotification();

  // --- FETCH DATA ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, coupRes, statsRes] = await Promise.all([
        fetch(
          `${API_BASE_URL}/admin/products/${id}`,
          { headers: getAuthHeaders() },
        ),
        fetch(
          `${API_BASE_URL}/admin/products/${id}/campaign-coupons`,
          { headers: getAuthHeaders() },
        ),
        fetch(
          `${API_BASE_URL}/admin/products/${id}/campaign-coupons/stats`,
          { headers: getAuthHeaders() },
        ),
      ]);

      const prodData = await prodRes.json();
      const coupData = await coupRes.json();
      const statsData = await statsRes.json();

      if (prodData.success) setProduct(prodData.data);
      if (coupData.success) setCoupons(coupData.data);
      if (statsData.success) setStats(statsData.data);
    } catch (error) {
      showError("Error", "Failed to load coupon data");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchData();
  }, [id, fetchData]);

  const handleDelete = async (couponId) => {
    if (
      !window.confirm(
        "WARNING: Delete this coupon? Users will no longer be able to use it.",
      )
    )
      return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/products/campaign-coupons/${couponId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Deleted", "Coupon removed");
        fetchData();
      } else showError("Failed", data.error);
    } catch (e) {
      showError("Error", "Delete failed");
    }
  };

  const isExpired = (date) => date && new Date(date) < new Date();

  return (
    <Box className="space-y-6">
      {/* HEADER */}
      <Group justify="space-between">
        <Group>
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() =>
              navigate(`/admin/dashboard/product-management/coupons/${id}`)
            }
          >
            Back
          </Button>
          <Box>
            <Text fw={800} size="xl" className="ota-gradient-text">
              Campaign Coupons
            </Text>
            {product && (
              <Text size="sm" c="dimmed">
                Product: {product.title}
              </Text>
            )}
          </Box>
        </Group>
        <Button
          leftSection={<IconPlus size={18} />}
          color="green"
          onClick={() => {
            setEditingCoupon(null);
            setShowModal(true);
          }}
        >
          New Coupon
        </Button>
      </Group>

      {/* STATS */}
      {stats && (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <StatsCard
            icon={<IconRocket size={24} />}
            label="Campaigns"
            value={stats.totalCoupons}
            color="green"
          />
          <StatsCard
            icon={<IconTarget size={24} />}
            label="Total Uses"
            value={stats.totalUses}
            color="blue"
            subValue={
              stats.totalMaxUses > 0
                ? `${stats.usageRate?.toFixed(1)}% utilized`
                : "Unlimited pool"
            }
          />
          <StatsCard
            icon={<IconPackage size={24} />}
            label="Max Capacity"
            value={stats.totalMaxUses > 0 ? stats.totalMaxUses : "∞"}
            color="purple"
          />
          <StatsCard
            icon={<IconCalendar size={24} />}
            label="Expired"
            value={stats.expiredCoupons}
            color="red"
          />
        </SimpleGrid>
      )}

      {/* LIST */}
      <Box pos="relative" mih={200}>
        <LoadingOverlay visible={loading} />

        {coupons.length === 0 && !loading ? (
          <Paper p="xl" withBorder radius="md" ta="center" bg="gray.0">
            <ThemeIcon
              size={60}
              radius="xl"
              color="gray"
              variant="light"
              mb="md"
            >
              <IconTicket size={30} />
            </ThemeIcon>
            <Text fw={600} size="lg">
              No Campaigns Active
            </Text>
            <Button
              mt="md"
              variant="white"
              onClick={() => {
                setEditingCoupon(null);
                setShowModal(true);
              }}
            >
              Create First Coupon
            </Button>
          </Paper>
        ) : (
          <Card withBorder radius="md" padding="0">
            <ScrollArea>
              <Table stickyHeader highlightOnHover>
                <Table.Thead bg="gray.1">
                  <Table.Tr>
                    <Table.Th>Code / Title</Table.Th>
                    <Table.Th>Discount</Table.Th>
                    <Table.Th>Usage</Table.Th>
                    <Table.Th>Expiry</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {coupons.map((coupon) => {
                    const expired = isExpired(coupon.expiryDate);
                    return (
                      <Table.Tr
                        key={coupon.id}
                        bg={expired ? "red.0" : undefined}
                      >
                        <Table.Td>
                          <Group gap="xs">
                            <Text fw={700} style={{ fontFamily: "monospace" }}>
                              {coupon.code}
                            </Text>
                            {coupon.isSuperCoupon && (
                              <Badge color="yellow" size="xs">
                                SUPER
                              </Badge>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {coupon.title}
                          </Text>
                          {coupon.ticketTypes?.length > 0 && (
                            <Group gap={4} mt={4}>
                              {coupon.ticketTypes.map((t) => (
                                <Badge
                                  key={t.id}
                                  color="grape"
                                  size="xs"
                                  variant="light"
                                >
                                  {t.title ||
                                    TICKET_TYPE_LABELS[t.ticketType] ||
                                    t.ticketType}
                                </Badge>
                              ))}
                            </Group>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {coupon.isFree ? (
                            <Badge color="green" variant="light">
                              FREE
                            </Badge>
                          ) : (
                            <Badge color="blue" variant="light">
                              {coupon.discountPercent}% OFF
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {coupon.currentUses} / {coupon.maxUses || "∞"}
                          </Text>
                          {coupon.imposedUserLimit && (
                            <Text size="xs" c="dimmed">
                              Limit: {coupon.perUserLimit} per user
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {coupon.expiryDate ? (
                            <Text size="sm" c={expired ? "red" : "dimmed"}>
                              {new Date(coupon.expiryDate).toLocaleDateString()}
                              {expired && " (Expired)"}
                            </Text>
                          ) : (
                            <Text size="sm" c="dimmed">
                              No Expiry
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td align="right">
                          <Group justify="flex-end" gap="xs">
                            <ActionIcon
                              variant="subtle"
                              color="blue"
                              onClick={() => {
                                setEditingCoupon(coupon);
                                setShowModal(true);
                              }}
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                            {coupon.currentUses === 0 && (
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(coupon.id)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>
        )}
      </Box>

      {/* CREATE/EDIT MODAL */}
      <CampaignCouponModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingCoupon(null);
        }}
        productId={id}
        initialData={editingCoupon}
        onSuccess={fetchData}
      />
    </Box>
  );
}
